import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button } from "components/common/Button";
import { formatQkindLabel } from "./formatQkindLabel";
import type { UnitDetails } from "./types";

interface UnitRowProps {
  unit: UnitDetails;
  selected: boolean;
  focused: boolean;
  id: string;
  onSelect: () => void;
  onMouseEnter: () => void;
  /** Open the info overlay, anchored to this row so its top edges line up. */
  onOpenInfo: (anchor: HTMLElement) => void;
  /** True while this row's info overlay is the one on screen. */
  infoOpen: boolean;
}

/**
 * One row in the picker's unit list. Grid: name | info | mono symbol | qkind tag.
 * `role="option"` participates in the parent listbox; `aria-selected` reflects
 * the staged pick. `data-row-uri` lets the list scroll-to-focus.
 *
 * The info button reveals on focus/hover and opens a pinned overlay. It is
 * deliberately click-driven: a hover-opened card cannot be moved into, because
 * MUI's popper computes `pointer-events: none` even when marked interactive.
 */
export function UnitRow({
  unit,
  selected,
  focused,
  id,
  onSelect,
  onMouseEnter,
  onOpenInfo,
  infoOpen,
}: UnitRowProps) {
  const display = unit.label ?? unit.alpha_code ?? unit.uri;
  const showInfo = focused || infoOpen;
  return (
    <div
      role="option"
      id={id}
      aria-selected={selected}
      data-row-uri={unit.uri}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: "var(--spacing-sm)",
        // Pin the box hard: `height` alone is treated as `flex-basis` by
        // some flex-column parents, which lets the row balloon when the
        // parent has leftover main-axis space. min/max equal forces the
        // computed height regardless of flex stretch or align-content
        // distribution. Reverts to a true 32 px row in every state.
        height: "var(--control-height-md)",
        minHeight: "var(--control-height-md)",
        maxHeight: "var(--control-height-md)",
        flexShrink: 0,
        padding: "0 var(--spacing-xs)",
        borderRadius: "var(--radius-app)",
        cursor: "pointer",
        fontSize: "var(--font-size)",
        background: selected
          ? "var(--color-accent-subtle)"
          : focused
            ? "var(--color-bg-hover)"
            : "transparent",
        color: selected ? "var(--color-accent)" : "var(--color-fg)",
        transition: "background var(--duration-micro)",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
          minWidth: 0,
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-2xs)",
            minWidth: 0,
          }}
        >
          <span
            style={{
              lineHeight: 1.1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {display}
          </span>
          {/* Space is always reserved so revealing the button never nudges the
              name sideways. */}
          <span
            style={{
              display: "inline-flex",
              flexShrink: 0,
              opacity: showInfo ? 1 : 0,
              pointerEvents: showInfo ? "auto" : "none",
              color: infoOpen ? "var(--color-accent)" : undefined,
              transition: "opacity var(--duration-micro)",
            }}
          >
            <Button
              variant="default"
              size="xs"
              aria-label={`Unit details for ${display}`}
              tabIndex={showInfo ? 0 : -1}
              onClick={(e) => {
                // The row selects on click; the button must not stage a pick.
                e.stopPropagation();
                onOpenInfo(e.currentTarget.closest("[data-row-uri]")!);
              }}
            >
              <InfoOutlinedIcon />
            </Button>
          </span>
        </span>
        {unit.symbol && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--font-size-small)",
              color: selected ? "var(--color-accent)" : "var(--color-fg-muted)",
            }}
          >
            {unit.symbol}
          </span>
        )}
      </span>
      {unit.quantity_kind_label && (
        <span
          style={{
            fontSize: "var(--font-size-xx-small)",
            color: selected ? "var(--color-accent)" : "var(--color-fg-muted)",
            border: `var(--size-border-hairline) solid ${selected ? "var(--color-accent)" : "var(--color-border)"}`,
            padding: "var(--spacing-2xs) var(--spacing-xs)",
            borderRadius: "var(--radius-app)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "12rem",
          }}
        >
          {formatQkindLabel(unit.quantity_kind_label)}
        </span>
      )}
    </div>
  );
}
